import { useEffect, useState, useRef } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useBrandAuthCheck } from '../../hooks/useBrandAuthCheck';
import { subscriptionService } from '../services/subscriptionService';
import { updateProfileViewUsage, selectSubscription } from '../../Redux/subscriptionSlice';
import InfluencerDetailsPage from './InfluencerDetailsPage';
import PricingPage from '../../modules/pricing/PricingPage';
import { useInfluencer } from '../hooks/useInfluencer';
import Navbar from '../../components/Navbar';
import RegisterModal from '../../components/RegisterModal';
import { clearUserState } from '../../Redux/userSlice';

const ProtectedInfluencerDetailsPage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isBrand, isAuthenticated } = useBrandAuthCheck();
  const { influencer, loading: influencerLoading } = useInfluencer(username);
  const subscription = useSelector(selectSubscription);
  const [loading, setLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [viewGate, setViewGate] = useState(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const trackViewCalledRef = useRef(false);

  const handleRegisterAs = (type) => {
    setShowJoinModal(false);
    if (type === 'influencer') {
      navigate('/influencer/login');
      return;
    }

    const userType = localStorage.getItem('userType');
    if (userType === 'influancer') {
      dispatch(clearUserState());
    }
    navigate('/company/login');
  };

  useEffect(() => {
    const checkAccess = async () => {
      if (influencerLoading) return;
      // Check if influencer exists
      if (!influencer) {
        setLoading(false);
        return;
      }

      // Check if user is authenticated as brand
      if (!isAuthenticated || !isBrand) {
        setLoading(false);
        setShowJoinModal(true);
        return;
      }

      try {
        if (!trackViewCalledRef.current) {
          trackViewCalledRef.current = true;
          const trackResponse = await subscriptionService.trackProfileView(username);
          const data = trackResponse?.data;

          setViewGate(data ?? null);

          if (!trackResponse?.success || !data?.canView) {
            setCanView(false);
            if (data?.requiresUpgrade) {
              toast.warning(
                `You've reached your daily limit of ${data.viewLimit} profile views. Upgrade to access more profiles.`
              );
            }
            setLoading(false);
            return;
          }

          dispatch(
            updateProfileViewUsage({
              viewLimit: data.viewLimit,
              viewsUsed: data.viewsUsed,
              viewsRemaining: data.viewsRemaining,
            })
          );

          setCanView(true);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error checking access:', error);
        toast.error('Failed to check access. Please try again.');
        setLoading(false);
      }
    };

    checkAccess();
  }, [username, influencer, influencerLoading, isAuthenticated, isBrand, dispatch]);

  if (influencerLoading || loading) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      </>
    );
  }

  if (!influencer) {
    return <Navigate to="/marketplace" replace />;
  }

  if (!isAuthenticated || !isBrand) {
    return (
      <>
        <RegisterModal
          isOpen={showJoinModal}
          onClose={() => navigate('/marketplace')}
          onRegisterAs={handleRegisterAs}
        />
      </>
    );
  }

  if (subscription?.planType === 'basic' || viewGate?.planType === 'basic') {
    return <PricingPage />;
  }

  if (!canView && viewGate !== null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            View Limit Reached
          </h2>
          <p className="text-gray-600 mb-6">
            You've reached your daily limit of {viewGate?.viewLimit ?? subscription?.viewLimit ?? 0} profile views.
            Upgrade your plan to access more profiles.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition font-semibold"
          >
            Upgrade Plan
          </button>
        </div>
      </div>
    );
  }

  return <InfluencerDetailsPage />;
};

export default ProtectedInfluencerDetailsPage;
